/**
 * Iris Knowledge Service
 *
 * Thin client for the server-side model knowledge base
 * (server/knowledge — curated per-model prompting best practices).
 *
 * Fails open: any network/API error returns null/[] so Iris keeps working
 * without knowledge snippets.
 */

import logger from '../utils/logger';

export interface KnowledgeSearchResult {
  id: string;
  title: string;
  kind: string;
  score: number;
  text: string;
}

export interface KnowledgeModelEntry {
  id: string;
  title: string;
  text: string;
}

const FETCH_TIMEOUT_MS = 3000;

async function fetchWithTimeout(url: string): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

class IrisKnowledgeService {
  /** modelId → entry (null = known miss, cached too) */
  private modelCache = new Map<string, KnowledgeModelEntry | null>();

  /**
   * Best-practices entry for a concrete model id (e.g. "flux-2-max").
   * Returns null when the KB has no entry or the request fails.
   */
  async getModelEntry(modelId: string | undefined | null): Promise<KnowledgeModelEntry | null> {
    if (!modelId || !modelId.trim()) return null;
    const key = modelId.trim().toLowerCase();

    if (this.modelCache.has(key)) return this.modelCache.get(key) ?? null;

    try {
      const response = await fetchWithTimeout(`/api/knowledge/model/${encodeURIComponent(key)}`);
      if (!response.ok) {
        this.modelCache.set(key, null);
        return null;
      }
      const data = await response.json();
      const entry: KnowledgeModelEntry | null = typeof data?.text === 'string'
        ? { id: String(data.id || ''), title: String(data.title || ''), text: data.text }
        : null;
      this.modelCache.set(key, entry);
      return entry;
    } catch (error) {
      logger.debug('[IrisKnowledge] model entry fetch failed:', error);
      return null; // do not cache transient failures
    }
  }

  /**
   * Keyword search over the KB (compact snippets). Used to inject relevant
   * model notes into Iris's context based on the user's message.
   */
  async search(query: string, limit = 2): Promise<KnowledgeSearchResult[]> {
    if (!query || !query.trim()) return [];
    try {
      const params = new URLSearchParams({ q: query.slice(0, 500), limit: String(limit) });
      const response = await fetchWithTimeout(`/api/knowledge/search?${params.toString()}`);
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data?.entries) ? data.entries : [];
    } catch (error) {
      logger.debug('[IrisKnowledge] search failed:', error);
      return [];
    }
  }
}

export const irisKnowledgeService = new IrisKnowledgeService();
export default irisKnowledgeService;
